import { useState, useRef, useEffect, useContext } from 'react';
import { Sparkles, RotateCcw, Loader2 } from 'lucide-react';
import { useNotification } from '@/components/notifications';
import { AuthContext } from '@/context/auth-context';
import { enhancePrompt } from '@/services/workflow/enhance-prompt';
import { useOrgStore } from '@/stores/org-store';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import './prompt-enhancer.css';

interface PromptEnhancerProps {
  value: string;
  onChange: (value: string) => void;
  onEnhancingChange?: (isEnhancing: boolean) => void;
  onEnhancementStart?: (originalText: string) => void; // Called before enhancement starts with the original text
  variant?: 'tooltip' | 'slide'; // 'tooltip' for textarea, 'slide' for modal
  className?: string; // Custom positioning classes
  workflowName?: string;
  workflowDescription?: string;
  nodeType?: string;
  nodeName?: string;
  originalText?: string; // Original text before enhancement, used to detect if already enhanced
}

export const PromptEnhancer: React.FC<PromptEnhancerProps> = ({
  value,
  onChange,
  onEnhancingChange,
  onEnhancementStart,
  variant = 'slide',
  className = '',
  workflowName = '',
  workflowDescription = '',
  nodeType = '',
  nodeName = '',
  originalText: originalTextProp,
}) => {
  const { user } = useContext(AuthContext);
  const notification = useNotification();
  const currentOrg = useOrgStore((state) => state.currentOrg);

  const [originalText, setOriginalText] = useState<string>(
    originalTextProp || '',
  );
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [isUndoMode, setIsUndoMode] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const typingAnimationRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );

  // Check if text is already enhanced when component mounts or value changes
  useEffect(() => {
    if (originalTextProp && value && value !== originalTextProp) {
      // Text has been enhanced (value differs from original)
      setIsUndoMode(true);
      setOriginalText(originalTextProp);
    } else if (originalTextProp && value === originalTextProp) {
      // Text matches original, not enhanced
      setIsUndoMode(false);
      setOriginalText(originalTextProp);
    }
  }, [originalTextProp, value]);

  // Notify parent of enhancing state changes
  useEffect(() => {
    onEnhancingChange?.(isEnhancing);
  }, [isEnhancing, onEnhancingChange]);

  // Cleanup animation on unmount
  useEffect(() => {
    return () => {
      if (typingAnimationRef.current) {
        clearInterval(typingAnimationRef.current);
      }
    };
  }, []);

  // Calculate animation speed based on text length
  // Longer texts animate faster to reduce wait time before backend call
  const calculateAnimationSpeed = (textLength: number, baseSpeed: number) => {
    // For short texts (< 100 chars): use base speed
    // For medium texts (100-500 chars): 2x faster
    // For long texts (500-1000 chars): 4x faster
    // For very long texts (1000-2000 chars): near-instant (1-2ms)
    // For extremely long texts (> 2000 chars): instant (0ms - skip animation)
    if (textLength < 100) {
      return baseSpeed;
    } else if (textLength < 500) {
      return Math.max(2, baseSpeed / 2);
    } else if (textLength < 1000) {
      return Math.max(1, baseSpeed / 4);
    } else if (textLength < 2000) {
      return 1; // Near-instant for large texts
    } else {
      return 0; // Instant - skip animation for very large texts
    }
  };

  // Typing animation utilities
  const reverseTypeAnimation = (
    text: string,
    callback: (newText: string) => void,
    onComplete: () => void,
  ) => {
    const baseSpeed = 30;
    const speed = calculateAnimationSpeed(text.length, baseSpeed);

    // For very large texts, skip animation entirely
    if (speed === 0) {
      callback('');
      onComplete();
      return;
    }

    let currentText = text;
    const interval = setInterval(() => {
      if (currentText.length === 0) {
        clearInterval(interval);
        onComplete();
        return;
      }
      currentText = currentText.slice(0, -1);
      callback(currentText);
    }, speed);
    typingAnimationRef.current = interval;
  };

  const typeAnimation = (
    targetText: string,
    callback: (newText: string) => void,
    onComplete: () => void,
  ) => {
    const baseSpeed = 20;
    const speed = calculateAnimationSpeed(targetText.length, baseSpeed);

    // For very large texts, skip animation entirely
    if (speed === 0) {
      callback(targetText);
      onComplete();
      return;
    }

    let currentText = '';
    let index = 0;
    const interval = setInterval(() => {
      if (index >= targetText.length) {
        clearInterval(interval);
        onComplete();
        return;
      }
      currentText += targetText[index];
      callback(currentText);
      index++;
    }, speed);
    typingAnimationRef.current = interval;
  };

  // Handle prompt enhancement
  const handleEnhancePrompt = async () => {
    const currentText = value.trim();
    if (!currentText) {
      notification.error('Please enter text to enhance');
      return;
    }

    if (!user?.email) {
      notification.error('User email not available. Please try again.');
      return;
    }

    // Validate required workflow and node information
    if (!workflowName || !nodeType || !nodeName) {
      notification.error(
        'Workflow and node information is required for enhancement.',
      );
      return;
    }

    // Store original text for undo
    setOriginalText(currentText);
    // Notify parent of the original text BEFORE starting enhancement
    // This ensures we capture the correct value before the reverse animation starts
    onEnhancementStart?.(currentText);
    setIsEnhancing(true);

    // Clear any existing animation
    if (typingAnimationRef.current) {
      clearInterval(typingAnimationRef.current);
    }

    // Reverse type animation - remove current text
    reverseTypeAnimation(
      currentText,
      (newText) => {
        onChange(newText);
      },
      async () => {
        // After text is cleared, call the API
        try {
          const response = await enhancePrompt({
            workflow_name: workflowName,
            workflow_description: workflowDescription || '',
            node_type: nodeType,
            node_name: nodeName,
            user_query: currentText,
            orgid: currentOrg?.orgid ?? '',
          });

          if (response?.detail) {
            notification.error(response.detail);
            // Restore original text on error
            onChange(currentText);
            setIsUndoMode(false);
            setIsEnhancing(false);
            return;
          }

          // Handle the API response - extract enhanced_query
          const responseData = response?.data as {
            enhanced_query?: string;
          };
          const enhancedQuery = responseData?.enhanced_query;

          if (!enhancedQuery) {
            notification.error(
              'No enhanced query was generated. Please try again or update your workflow description.',
            );
            onChange(currentText);
            setIsUndoMode(false);
            setIsEnhancing(false);
            return;
          }

          // Type animation - show enhanced text
          typeAnimation(
            enhancedQuery,
            (newText) => {
              onChange(newText);
            },
            () => {
              // After animation completes, update the field value
              setIsUndoMode(true);
              setIsEnhancing(false);
              notification.success('Prompt enhanced successfully!');
            },
          );
        } catch (error) {
          console.error('Error enhancing prompt:', error);
          notification.error('Failed to enhance prompt. Please try again.');
          // Restore original text on error
          onChange(currentText);
          setIsUndoMode(false);
          setIsEnhancing(false);
        }
      },
    );
  };

  // Handle undo to restore original text
  const handleUndo = () => {
    // Use originalTextProp if available, otherwise use internal originalText state
    const textToRestore = originalTextProp || originalText;
    if (!textToRestore) return;

    // Clear any existing animation
    if (typingAnimationRef.current) {
      clearInterval(typingAnimationRef.current);
    }

    // Reverse type animation - remove enhanced text
    reverseTypeAnimation(
      value,
      (newText) => {
        onChange(newText);
      },
      () => {
        // Type animation - show original text
        typeAnimation(
          textToRestore,
          (newText) => {
            onChange(newText);
          },
          () => {
            // After animation completes
            setIsUndoMode(false);
            // Only clear internal state if we're not using the prop
            if (!originalTextProp) {
              setOriginalText('');
            }
          },
        );
      },
    );
  };

  // Don't render if user doesn't have email
  if (!user?.email) {
    return null;
  }

  const icon = isEnhancing ? (
    <Loader2 className='h-4 w-4 animate-spin' />
  ) : isUndoMode ? (
    <RotateCcw className='h-4 w-4' />
  ) : (
    <Sparkles className={`h-4 w-4 ${isHovered ? 'text-blue-500' : ''}`} />
  );

  const labelText = isEnhancing
    ? 'Generating'
    : isUndoMode
      ? 'Undo'
      : 'Prompt enhancer';

  // Tooltip variant (for textarea)
  if (variant === 'tooltip') {
    return (
      <div className={className}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              onClick={isUndoMode ? handleUndo : handleEnhancePrompt}
              disabled={isEnhancing}
              className='h-4 w-4 bg-transparent hover:bg-accent text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50'
            >
              {icon}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{isUndoMode ? 'Undo enhancement' : 'Prompt enhancer'}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }

  // Slide variant (for modal) - label inside button
  return (
    <div className={className}>
      <Button
        variant='ghost'
        size='lg'
        onClick={isUndoMode ? handleUndo : handleEnhancePrompt}
        disabled={isEnhancing}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className='h-full w-full bg-transparent hover:bg-accent text-muted-foreground hover:text-foreground gap-1 hover:gap-1.5 px-0 py-1 hover:px-2 transition-all duration-300 disabled:opacity-50'
      >
        {icon}
        {/* Hover label */}
        <span
          className={`text-xs whitespace-nowrap transition-all duration-300 ${
            isHovered
              ? 'max-w-[110px] opacity-100 pr-1'
              : 'max-w-0 opacity-0 overflow-hidden'
          } ${
            // Apply shimmer effect:
            // - Always when isEnhancing (showing "generating")
            // - On hover when not enhancing and not in undo mode (showing "Prompt enhancer")
            // - Never when in undo mode
            isEnhancing || (!isUndoMode && isHovered)
              ? 'shimmer-text-subtle'
              : 'text-muted-foreground'
          }`}
        >
          {labelText}
        </span>
      </Button>
    </div>
  );
};
